import React, { useEffect } from 'react';
import { 
  ShieldCheck, ShieldAlert, Video, Users, 
  X, Fingerprint, Award, ArrowRight
} from 'lucide-react';
import useDragToScroll from '../../hooks/useDragToScroll';

const InvestigationReportModal = ({
  isOpen,
  onClose,
  reportData,
  userProfile,
  attackerProfile,
  parentTiles,
  userXP = 40,
  userPV = 55,
}) => {
  const containerRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const cardRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        if (cardRef.current) {
          cardRef.current.scrollTop = 0;
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen || !reportData) return null;

  const isIdentified = Boolean(reportData?.isIdentified);
  const fleePct = reportData?.fleeProgressPct !== undefined ? reportData.fleeProgressPct : 100;
  const isAttackInterrupted = fleePct < 100;
  const alertTriggered = Boolean(reportData?.alertTriggered);
  const phoneLocation = reportData?.phoneLocation || 'in_hand';

  // 1. TEXTE OFFICIEL SUR L'AGRESSION (POSITIF EN VERT SI MIS EN FUITE)
  const attackStatusText = !isAttackInterrupted
    ? "❌ L'agresseur n'a pas pu être interrompu à temps et a réussi son agression (100% du forfait accompli)."
    : `✓ L'agresseur a pris la fuite face à l'intervention des Protecteurs (agression interrompue à ${fleePct}% de son forfait).`;

  // 2. TEXTE OFFICIEL SUR L'IDENTIFICATION / ARRESTATION (STRICTEMENT CONDITIONNÉE PAR LA VIDÉO EN MAIN)
  let arrestStatusText = "";
  if (isIdentified) {
    arrestStatusText = "Identifié & Arrêté : Grâce aux enregistrements vidéo HD transmis en direct au cloud MonAlerte, l'individu a été formellement identifié et localisé par les forces de l'ordre.";
  } else {
    if (phoneLocation === 'in_bag' || phoneLocation === 'in_pocket') {
      const locLabel = phoneLocation === 'in_bag' ? 'le sac' : 'la poche';
      arrestStatusText = `Non identifié / En fuite : Le smartphone est resté dans ${locLabel} (objectif masqué dans l'obscurité, impossible de filmer le visage du suspect).`;
    } else {
      arrestStatusText = "Non identifié / En fuite : Aucune capture vidéo en main n'a permis de relever le visage du suspect.";
    }
  }

  return (
    <div
      ref={containerRef}
      className="report-modal-overlay"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(3, 7, 18, 0.78)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        borderRadius: '16px',
        padding: '8px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        ref={cardRef}
        style={{
          background: 'linear-gradient(160deg, rgba(13, 27, 46, 0.98) 0%, rgba(6, 11, 20, 0.99) 100%)',
          border: '2px solid rgba(242, 184, 7, 0.75)',
          borderRadius: '14px',
          maxWidth: '780px',
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '14px 16px',
          boxSizing: 'border-box',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.95), 0 0 25px rgba(242, 184, 7, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          position: 'relative',
          animation: 'slideUpReport 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        <style>{`
        @keyframes slideUpReport {
          0% {
            opacity: 0;
            transform: translateY(35px) scale(0.98);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @media (max-width: 860px) {
          .report-modal-overlay {
            align-items: flex-start !important;
            padding-top: 6px !important;
          }
        }
      `}</style>

      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: 'none',
            color: '#cbd5e1',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <X size={18} />
        </button>

        {/* EN-TÊTE DU RAPPORT */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '14px',
            background: 'rgba(242, 184, 7, 0.2)',
            border: '1.5px solid var(--mga-gold)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Fingerprint size={28} color="var(--mga-gold)" />
          </div>
          <div>
            <span style={{
              background: 'rgba(242, 184, 7, 0.2)',
              color: 'var(--mga-gold)',
              border: '1px solid rgba(242, 184, 7, 0.4)',
              padding: '2px 8px',
              borderRadius: '6px',
              fontSize: '0.68rem',
              fontWeight: 900,
              textTransform: 'uppercase',
            }}>
              RAPPORT DE FIN D'INCIDENT & ENQUÊTE
            </span>
            <h2 style={{ fontSize: '1.4rem', color: '#ffffff', margin: '4px 0 0 0', fontWeight: 900 }}>
              Résultats de la Simulation
            </h2>
          </div>
        </div>

        {/* CATÉGORIE 1 : DÉROULEMENT DE L'AGRESSION */}
        <div style={{
          background: isAttackInterrupted ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          border: '1.5px solid ' + (isAttackInterrupted ? '#10b981' : '#ef4444'),
          borderRadius: '18px',
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{
              fontSize: '0.74rem',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: isAttackInterrupted ? '#4ade80' : '#f87171',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              {isAttackInterrupted ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
              1. DÉROULEMENT DE L'AGRESSION
            </span>
            <span style={{
              background: isAttackInterrupted ? '#10b981' : '#ef4444',
              color: isAttackInterrupted ? '#000000' : '#ffffff',
              padding: '3px 10px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 900,
            }}>
              {isAttackInterrupted ? `INTERROMPUE (${fleePct}%)` : 'NON INTERROMPUE (100%)'}
            </span>
          </div>

          <p style={{
            fontSize: '1rem',
            color: '#ffffff',
            fontWeight: 700,
            lineHeight: 1.5,
            margin: 0,
          }}>
            {attackStatusText}
          </p>
        </div>

        {/* CATÉGORIE 2 : IDENTIFICATION & ARRESTATION DU SUSPECT */}
        <div style={{
          background: isIdentified ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          border: '1.5px solid ' + (isIdentified ? '#10b981' : '#ef4444'),
          borderRadius: '18px',
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{
              fontSize: '0.74rem',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: isIdentified ? '#4ade80' : '#f87171',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              {isIdentified ? (reportData?.identificationMethod === 'video' ? <Video size={16} /> : <Users size={16} />) : <Fingerprint size={16} />}
              2. IDENTIFICATION & ARRESTATION DU SUSPECT
            </span>
            <span style={{
              background: isIdentified ? '#10b981' : '#ef4444',
              color: isIdentified ? '#000000' : '#ffffff',
              padding: '3px 10px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 900,
            }}>
              {isIdentified ? 'ARRÊTÉ & IDENTIFIÉ' : 'NON IDENTIFIÉ / EN FUITE'}
            </span>
          </div>

          <p style={{
            fontSize: '1rem',
            color: '#ffffff',
            fontWeight: 700,
            lineHeight: 1.5,
            margin: 0,
          }}>
            {arrestStatusText}
          </p>
        </div>

        {/* CATÉGORIE 3 : RÉCOMPENSES XP & PV GAGNÉES */}
        {reportData?.xpReward && (
          <div style={{
            background: 'rgba(242, 184, 7, 0.08)',
            border: '1.5px solid rgba(242, 184, 7, 0.35)',
            borderRadius: '18px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{
                fontSize: '0.74rem',
                fontWeight: 900,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--mga-gold)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <Award size={16} /> 3. BILAN DES GAINS DE LA PARTIE
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.85rem', color: '#fca5a5', fontWeight: 900, background: 'rgba(239, 68, 68, 0.25)', padding: '2px 8px', borderRadius: '6px' }}>
                  +{reportData.xpReward.totalPV || 0} PV
                </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--mga-gold)', fontWeight: 900, background: 'rgba(242, 184, 7, 0.25)', padding: '2px 8px', borderRadius: '6px' }}>
                  +{reportData.xpReward.totalXP || 0} XP
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {reportData.xpReward.pvBreakdown?.map((item, idx) => (
                <span key={`pv-${idx}`} style={{
                  fontSize: '0.74rem',
                  background: 'rgba(239, 68, 68, 0.15)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontWeight: 700,
                }}>
                  💖 {item.label}
                </span>
              ))}

              {reportData.xpReward.xpBreakdown?.map((item, idx) => (
                <span key={`xp-${idx}`} style={{
                  fontSize: '0.74rem',
                  background: item.highlight ? 'rgba(74, 222, 128, 0.15)' : 'rgba(242, 184, 7, 0.12)',
                  color: item.highlight ? '#86efac' : '#ffd873',
                  border: '1px solid ' + (item.highlight ? 'rgba(74, 222, 128, 0.35)' : 'rgba(242, 184, 7, 0.25)'),
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontWeight: 700,
                }}>
                  ⭐ {item.label}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '4px', fontSize: '0.82rem', color: 'var(--mga-slate-300)' }}>
              <span>Nouveau Solde Vitalité : <strong style={{ color: '#fca5a5' }}>{reportData.userPV ?? userPV} PV</strong></span>
              <span>Nouveau Solde XP : <strong style={{ color: 'var(--mga-gold)' }}>{reportData.userXP ?? userXP} XP</strong></span>
            </div>
          </div>
        )}

        {/* BOUTON D'ACTION PRINCIPAL */}
        <button
          onClick={onClose}
          style={{
            background: 'linear-gradient(135deg, #f2b807 0%, #d97706 100%)',
            color: '#000000',
            border: 'none',
            padding: '14px',
            borderRadius: '14px',
            fontWeight: 900,
            fontSize: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: '0 4px 18px rgba(242, 184, 7, 0.3)',
            marginTop: '4px',
          }}
        >
          <span>Continuer / Fermer le Rapport</span>
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default InvestigationReportModal;
